import { useTranslation } from "react-i18next";
import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import {
	Button,
	message,
	Modal,
	Typography
} from 'antd';
import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routesConfig } from '../../../config/routes';
import useKeyword from '../../../hooks/useKeyword';
import CreateFolderDrawer from '../../../pages/library/learning-repository/components/create-folder-drawer/CreateFolderDrawer';
import LearningMaterialView from '../../../pages/library/learning-repository/components/learning-material-view/LearningMaterialView';
import { createLearningFolder } from '../../../service/learning-material';
import { LearningMaterialStore, useLearningRepositoryStore } from '../../../stores/stores';
import { LearningMaterialV2Type } from '../../../types/learning-material';
import { notifyError } from '../../../utils/notification';
import SmartBreadcrumb, { BreadcrumbItem } from '../../breadcrumb/SmartBeadcrumb';
import FormInput from '../../form-input/FormInput';
import "./ChooseLearningMaterialModal.scss";

interface Props {
	open: boolean;
	onCancel: () => void;
	onSelectLearningMaterial?: (material: LearningMaterialV2Type) => void;
	type?: string;
	mode?: "select" | "saveTemplate" | "moveItem";
	onSaveTemplate?: (data: { folderId: any }) => void;
	isSaving?: boolean;
	selectedItems?: LearningMaterialV2Type[];
	movedId?: number | null;
	fixedTab?: "1" | "2";
}

const ChooseLearningMaterialModal: React.FC<Props> = ({
	open,
	onCancel,
	onSelectLearningMaterial,
	type,
	onSaveTemplate,
	mode = "select",
	isSaving,
	selectedItems = [],
	movedId,
	fixedTab
}) => {
  const { t } = useTranslation();
	const { folderInfo, keyword, set, reset, tab } = useLearningRepositoryStore((s) => ({
		keyword: s.keyword,
		set: s.set,
		folderInfo: s.folderInfo,
		reset: s.reset,
		tab: s.tab as "1" | "2"
	}));

	const [showFilter, setShowFilter] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const [selected, setSelected] = useState<LearningMaterialV2Type | null>(null);
	const folderId = useKeyword("folderId");
	const { onReload } = LearningMaterialStore();
	const [drawerState, setDrawerState] =
		useState<null | { type: 'lesson' | 'folder', id?: number, module?: any, edit?: boolean }>(null);

	useEffect(() => {
		if (open) {
			setSearchValue('');
			setSelected(null);
			setShowFilter(false);
			if (fixedTab) {
				set("tab", fixedTab);
			}
		}
	}, [open, fixedTab]);

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
	};

	const handleSubmitSearch = () => {
		set("keyword", searchValue);
		set("page", 1);
	};

	const handleSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleSubmitSearch();
	};

	const location = useLocation();
	const navigate = useNavigate();
	const params = new URLSearchParams(location.search);

	const handleCancel = () => {
		params.delete("folderId");
		params.delete("isMine");
		params.delete("isShared");
		reset();
		setSearchValue('');
		navigate({
			pathname: location.pathname,
			search: params.toString(),
		});

		onCancel();
	};

	const isSaveDisabled = () => {
		if (mode === "select") {
			return !selected;
		}
		else if (mode === "moveItem") {
			const hasItemsToMove = (selectedItems && selectedItems.length > 0) || movedId;

			if (!hasItemsToMove) {
				return true;
			}

			let movingItemsTab: "1" | "2" | null = null;
			if (selectedItems.length > 0) {
				movingItemsTab = selectedItems[0]?.isMine ? "1" : "2";
			}

			const isInFolder = folderId || (tab === movingItemsTab);
			if (movingItemsTab && tab && movingItemsTab !== tab && !isInFolder) {
				return true;
			}

			return false;
		}
		return false;
	};

	const getDisabledTooltip = () => {
		if (mode === "moveItem" && isSaveDisabled()) {
			const hasItemsToMove = (selectedItems && selectedItems.length > 0) || movedId;

			if (!hasItemsToMove) {
				return t("Chưa chọn mục để di chuyển", "Chưa chọn mục để di chuyển");
			}

			let movingItemsTab: "1" | "2" | null = null;
			if (selectedItems.length > 0) {
				movingItemsTab = selectedItems[0]?.isMine ? "1" : "2";
			}

			const isInFolder = folderId || (tab === movingItemsTab);

			if (movingItemsTab && tab && movingItemsTab !== tab && !isInFolder) {
				const targetTabName = movingItemsTab === "1" ? t("Của tôi", "Của tôi") : t("Được chia sẻ", "Được chia sẻ");
				return `Không thể di chuyển mục từ tab "${targetTabName}" sang tab này ở cấp ngoài cùng`;
			}
		}
		return "";
	};

	const footerJSX = (
		<div className='flex gap-8'>
			<Button
				type="primary"
				disabled={isSaveDisabled()}
				onClick={() => {
					if (mode === "select" && selected) {
						onSelectLearningMaterial?.(selected);
						handleCancel();
					} else if (mode === "saveTemplate" || mode === "moveItem") {
						onSaveTemplate?.({
							folderId: folderId ? Number(folderId) : null,
						});
					}
				}}
				loading={isSaving}
				title={getDisabledTooltip()}
			>
				{mode === "moveItem" ? t("Di chuyển", "Di chuyển") : t("Lưu", "Lưu")}
			</Button>
		</div>
	);

	const buildBreadcrumbItems = (): BreadcrumbItem[] => {
		const items: BreadcrumbItem[] =
			folderInfo?.parentFolders?.map(f => ({ id: f.id, name: f.name })) || [];

		if (
			folderId &&
			folderInfo?.name &&
			items.at(-1)?.id !== folderInfo.id
		) {
			items.push({ id: folderInfo.id, name: folderInfo.name });
		}
		return items;
	};

	const renderBreadcrumb = () => <SmartBreadcrumb
		basePath={`/${routesConfig.courseLibrary}/${routesConfig.learningRepository}`}
		items={buildBreadcrumbItems()}
		onlyQuery={true}
		onItemClick={(item) => {

			if (item.id === 'root') {
				params.delete("isMine");
				params.delete("isShared");
				set("tab", "1");
				params.set("folderId", "");

			}

			else {
				params.set("folderId", item.id);

				if (item.isMine) {
					params.set("isMine", "true");
					params.delete("isShared");
					set("tab", "1");
				} else {
					params.set("isShared", "true");
					set("tab", "2");
					params.delete("isMine");
				}
			}
			navigate({
				pathname: window.location.pathname,
				search: params.toString(),
			});
		}}
	/>

	const handleAddFolder = async (values: any) => {
		const payload = {
			name: values.name,
			accessLevel: Number(values.generalAccess),
			access: Number(values.generalPermission),
			authors: values.users.map((u: any) => ({ userId: u.id, permission: Number(u.permission) })),
			parentId: folderId || undefined,
		};
		try {
			await createLearningFolder(payload);
			message.success(`${drawerState?.id ? t("Sửa", "Sửa") : t("Thêm", "Thêm")} thư mục thành công`);
			setDrawerState(null);
			onReload();
		} catch {
			notifyError('Lỗi khi lưu thư mục');
		}
	}

	const getModalTitle = () => {
		if (mode === "moveItem") {
			const itemCount = movedId ? 1 : selectedItems.length;
			return `Di chuyển ${itemCount} mục đến`;
		}
		return t("Kho học liệu", "Kho học liệu");
	};

	return (
		<Modal
			title={
				<div className='flex justify-between align-center flex-wrap mr-6'>
					<div>
						<span>{getModalTitle()}</span>
						{renderBreadcrumb()}
					</div>
					<div className="km-header flex justify-between gap-8">
						<Button className="filter-button" onClick={() => setShowFilter((s) => !s)}>
							<Typography.Text>{t("Bộ lọc", "Bộ lọc")}</Typography.Text>
							<DownOutlined />
						</Button>

						<FormInput
							placeholder={t("Nhập tên để tìm kiếm", "Nhập tên để tìm kiếm")}
							value={searchValue}
							onChange={handleChangeInput}
							onKeyPress={handleSearchKey}
							afterPrefixIcon={<SearchOutlined onClick={handleSubmitSearch} />}
							style={{ width: 400 }}
						/>
						{(mode === "saveTemplate" || mode === "moveItem") && folderInfo?.isCanEdit !== false ? (
							<Button
								type="primary"
								onClick={() => {
									setDrawerState({ type: 'folder' })
								}}
							>
								{t("Thêm thư mục", "Thêm thư mục")}
							</Button>
						) : null}
					</div>
				</div>
			}
			open={open}
			onCancel={handleCancel}
			footer={null}
			width="90vw"
			centered
			destroyOnClose
			wrapClassName="choose-learning-material-modal"
		>
			<LearningMaterialView
				folderId={folderId}
				showFilter={showFilter}
				onCloseFilter={() => setShowFilter(false)}
				disableActions
				selectOne={mode === "select"}
				selectedRow={selected ? [selected] : []}
				onSelectRow={(rows) => setSelected(rows[0] || null)}
				moduleFilter={type === 'audio' ? 'mp3' : type}
				tabBarExtraContent={<div>{footerJSX}</div>}
				modalMode={mode}
				disableTabChange={!!fixedTab}
				movedId={movedId}
				movingItems={selectedItems}
			/>

			{drawerState?.type === 'folder' && (
				<CreateFolderDrawer
					open
					handleClose={() => setDrawerState(null)}
					onSubmit={(values) => {
						handleAddFolder(values)
					}}
					folderId={drawerState.id}
				/>
			)}
		</Modal>
	);
};

export default ChooseLearningMaterialModal;